import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Bell, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export function OnboardingToast({ onNavigate }) {
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotification = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/notifications/onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.notifications && response.data.notifications.length > 0) {
        const notif = response.data.notifications[0];
        setNotification(notif);
        setIsVisible(true);
        
        // Mark as triggered
        await axios.post(
          `${API}/notifications/onboarding/${notif.id}/trigger`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {}); // Ignore errors
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Small delay before showing notification to let page load
    const timer = setTimeout(() => {
      fetchNotification();
    }, 1500);

    return () => clearTimeout(timer);
  }, [fetchNotification]);

  const handleDismiss = async () => {
    if (!notification) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notifications/onboarding/${notification.id}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
    
    setIsVisible(false);
    setTimeout(() => setNotification(null), 300);
  };

  const handleAction = () => {
    if (!notification) return;
    
    if (notification.button_action === 'navigate' && notification.button_target) {
      // Call onNavigate if provided for activity library specific handling
      if (notification.button_target === '/activities' && onNavigate) {
        onNavigate();
      } else {
        navigate(notification.button_target);
      }
    }
    
    handleDismiss();
  };

  if (isLoading || !notification || !isVisible) {
    return null;
  }

  // Format message with line breaks
  const formattedMessage = notification.message.split('\n').map((line, i) => (
    <span key={i}>
      {line}
      {i < notification.message.split('\n').length - 1 && <br />}
    </span>
  ));

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 right-4 z-50 max-w-sm"
          data-testid="onboarding-toast"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-semibold text-white text-sm">{notification.title}</h3>
              </div>
              <button
                onClick={handleDismiss}
                className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                data-testid="dismiss-notification-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">
                {formattedMessage}
              </p>
              
              {/* Action Button */}
              <Button
                onClick={handleAction}
                className="w-full mt-4 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                data-testid="notification-action-btn"
              >
                {notification.button_text}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Notification Panel Component (for viewing all notifications)
export function NotificationsPanel({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchAllNotifications();
    }
  }, [isOpen]);

  const fetchAllNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/onboarding/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (notif.dismissed) return;
    
    // Navigate if action is navigate
    if (notif.button_action === 'navigate' && notif.button_target) {
      navigate(notif.button_target);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
          onClick={e => e.stopPropagation()}
          data-testid="notifications-panel"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-stone-900">Getting Started</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
              data-testid="close-panel-btn"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-64px)]">
            {isLoading ? (
              <div className="text-center py-8 text-stone-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-stone-500">No notifications</div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`p-4 rounded-xl border transition-all ${
                    notif.dismissed
                      ? 'bg-stone-50 border-stone-200 opacity-60'
                      : 'bg-white border-violet-200 hover:border-violet-300 cursor-pointer hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notif.dismissed ? 'bg-stone-200' : 'bg-violet-100'
                    }`}>
                      <Sparkles className={`w-5 h-5 ${notif.dismissed ? 'text-stone-400' : 'text-violet-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium ${notif.dismissed ? 'text-stone-500' : 'text-stone-900'}`}>
                        {notif.title}
                      </h3>
                      <p className={`text-sm mt-1 line-clamp-2 ${notif.dismissed ? 'text-stone-400' : 'text-stone-600'}`}>
                        {notif.message.split('\n')[0]}
                      </p>
                      {notif.dismissed && (
                        <span className="text-xs text-stone-400 mt-2 inline-block">Completed</span>
                      )}
                    </div>
                    {!notif.dismissed && (
                      <ChevronRight className="w-5 h-5 text-violet-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Bell icon button with notification badge
export function NotificationBell({ onClick, hasUnread = false }) {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
      data-testid="notification-bell-btn"
    >
      <Bell className="w-5 h-5 text-stone-600" />
      {hasUnread && (
        <span className="absolute top-1 right-1 w-2 h-2 bg-violet-500 rounded-full" />
      )}
    </button>
  );
}

export default OnboardingToast;
